/**
 * Unsaved-changes registry shared by both designs.
 *
 * Forms report whether they hold changes that are not on the server yet; the design switch
 * (DesignContext.setDesign) asks for confirmation instead of silently discarding them, because
 * switching design replaces the whole screen tree.
 */
import { useEffect, useRef } from "react";

const owners = new Set<object>();

export function setUnsaved(owner: object, dirty: boolean): void {
  if (dirty) owners.add(owner);
  else owners.delete(owner);
}

export function hasUnsavedChanges(): boolean {
  return owners.size > 0;
}

/** After the doctor chose to discard (e.g. confirmed a design switch). */
export function clearUnsaved(): void {
  owners.clear();
}

/** Reports `dirty` while the component is mounted. */
export function useUnsavedChanges(dirty: boolean): void {
  const owner = useRef({});
  useEffect(() => {
    const o = owner.current;
    setUnsaved(o, dirty);
    return () => setUnsaved(o, false);
  }, [dirty]);
}

/** Stable text form of form state; files count by name/size/type, functions are ignored. */
export function snapshotOf(value: unknown): string {
  try {
    return JSON.stringify(value, (_key, v) => {
      if (typeof File !== "undefined" && v instanceof File) return { file: v.name, size: v.size, type: v.type };
      if (typeof Blob !== "undefined" && v instanceof Blob) return { blob: v.size, type: v.type };
      if (typeof v === "function") return undefined;
      return v;
    }) ?? "";
  } catch {
    return "";
  }
}

/**
 * Dirty when `value` differs from what it was just before the doctor's first interaction with the
 * page (pointer or keyboard). Values filled in by loaders before that moment are not changes.
 * Returns `markSaved()` to call after a successful save when the screen stays open.
 */
export function useUnsavedFormGuard(value: unknown, enabled = true): { dirty: boolean; markSaved: () => void } {
  const latest = useRef("");
  latest.current = snapshotOf(value);
  const baseline = useRef<string | null>(null);

  useEffect(() => {
    const onInteract = () => {
      if (baseline.current === null) baseline.current = latest.current;
    };
    document.addEventListener("pointerdown", onInteract, true);
    document.addEventListener("keydown", onInteract, true);
    return () => {
      document.removeEventListener("pointerdown", onInteract, true);
      document.removeEventListener("keydown", onInteract, true);
    };
  }, []);

  const dirty = enabled && baseline.current !== null && latest.current !== baseline.current;
  useUnsavedChanges(dirty);
  return {
    dirty,
    markSaved: () => {
      baseline.current = null;
    },
  };
}

type PendingDoc = { file?: File | Blob | null; uploaded?: boolean } | null | undefined;

/** A document picked on the device that has not reached the server yet. */
export function hasPendingFiles(...docs: Array<PendingDoc | PendingDoc[]>): boolean {
  return docs.some((d) => (Array.isArray(d) ? d.some((x) => !!x?.file && !x.uploaded) : !!d?.file && !d.uploaded));
}
